import type { SchemaData } from '@lemonade-hq/chat-engine';
import { useOnAnswersChanged } from '@lemonade-hq/chat-wizard-client-base';
import type { WizardNavigationContextValue } from '@lemonade-hq/chat-wizard-client-base';
import type { GenericSchemaDataValue } from '@lemonade-hq/maschema-schema';
import { getUUID } from '@lemonade-hq/nuiza';
import type { BaseEventPayload, CommonAnalyticsParams } from '@lemonade-hq/uniclient-app';
import {
  getClientForTracking,
  getEventPrefix,
  MISSING_PRODUCT,
  useUniclientTrackingContext,
} from '@lemonade-hq/uniclient-app';
import type { UniclientFrame, UniclientWizardComponentBase } from '@lemonade-hq/uniclient-chat';
import get from 'lodash/get';
import { useEffect, useMemo, useRef } from 'react';
export interface UseWizardStepTrackingProps {
  readonly nav: WizardNavigationContextValue;
  readonly currentStep: CommonAnalyticsParams['currentStep'];
  readonly currentStepAnswers: CommonAnalyticsParams['currentStepAnswers'];
  readonly answersSchema: CommonAnalyticsParams['answersSchema'];
  readonly scriptName: BaseEventPayload['scriptName'];
  readonly sessionId?: BaseEventPayload['sessionId'];
  readonly userPublicId: CommonAnalyticsParams['userPublicId'];
  readonly isSubmitting: CommonAnalyticsParams['isSubmitting'];
  readonly currentStepFrame?: UniclientFrame;
  readonly componentsType?: UniclientWizardComponentBase['type'];
}

export function useWizardStepTracking({
  nav,
  currentStep,
  currentStepAnswers,
  answersSchema,
  scriptName,
  sessionId,
  isSubmitting,
  userPublicId,
  currentStepFrame,
  componentsType,
}: UseWizardStepTrackingProps): void {
  const lastTrackedStepIndex = useRef<number>(Number.NaN);
  const lastScriptTracked = useRef<string>('');

  // common payload for all chat events
  const commonEventPayload = useMemo(
    () => ({
      scriptName,
      stepName: currentStep?.name ?? '<UNKNOWN>',
      currentStepIndex: nav.currentStepIndex + 1,
      product: currentStep?.analytics?.product ?? MISSING_PRODUCT,
      clientUuid: getUUID(),
      client: getClientForTracking(),
      sessionId: sessionId ?? '<UNKNOWN>',
      gtmParams: currentStep?.analytics?.gtmParams,
      ...currentStep?.analytics?.params,
    }),
    [
      currentStep?.analytics?.params,
      currentStep?.analytics?.gtmParams,
      currentStep?.analytics?.product,
      currentStep?.name,
      nav.currentStepIndex,
      scriptName,
      sessionId,
    ],
  );

  const eventPrefix = useMemo(() => {
    if (currentStepFrame?.type === 'page' && currentStepFrame.eventPrefix != null) {
      return currentStepFrame.eventPrefix;
    }

    return getEventPrefix(currentStepFrame?.type, componentsType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentsType, currentStepFrame?.type]);

  const commonAnalyticsParams = useMemo(
    () => ({
      currentStep,
      currentStepAnswers,
      answersSchema,
      isSubmitting,
      userPublicId,
      eventPrefix,
    }),
    [currentStep, currentStepAnswers, answersSchema, isSubmitting, userPublicId, eventPrefix],
  );
  const { initTrackingParams, trackStepViewed, trackEdit, trackEvent } = useUniclientTrackingContext();

  useEffect(() => {
    initTrackingParams(commonEventPayload, commonAnalyticsParams);

    const isInvalidStepIndex = Number.isNaN(nav.currentStepIndex);

    if (
      !isInvalidStepIndex &&
      currentStep != null &&
      answersSchema != null &&
      lastTrackedStepIndex.current !== nav.currentStepIndex
    ) {
      lastTrackedStepIndex.current = nav.currentStepIndex;

      trackStepViewed();
    }

    if (commonEventPayload.scriptName !== lastScriptTracked.current) {
      lastScriptTracked.current = commonEventPayload.scriptName;
      trackEvent('chat.script_started', {
        scriptName: commonEventPayload.scriptName,
      });
    }

    if (isInvalidStepIndex) {
      lastTrackedStepIndex.current = Number.NaN;
    }
  }, [
    currentStep,
    answersSchema,
    nav.currentStepIndex,
    trackStepViewed,
    initTrackingParams,
    commonEventPayload,
    commonAnalyticsParams,
    trackEvent,
  ]);

  useOnAnswersChanged(
    currentStepAnswers,
    modifiedAnswerKeys => {
      const trackedAttributes = currentStep?.analytics?.trackedAttributes ?? [];

      if (trackedAttributes.some((a: string) => modifiedAnswerKeys.includes(a))) {
        const modifiedAnswers = modifiedAnswerKeys.reduce(
          (acc, key) => ({ ...acc, [key]: get(currentStepAnswers, key) as GenericSchemaDataValue }),
          {} as SchemaData,
        );
        void trackEdit(modifiedAnswers);
      }
    },
    currentStep?.data,
  );
}
